import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/api';

const AuthModal = ({ onUserLoaded, isGuestMode, onExitGuestMode, theme, onUserChange }) => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState('login'); // 'login' | 'register' | 'forgot'
  const [usePinLogin, setUsePinLogin] = useState(false);

  // Form fields
  const [inputId, setInputId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Settings dropdown
  const [showSettings, setShowSettings] = useState(false);

  // Settings dialogs
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [showSetPinDialog, setShowSetPinDialog] = useState(false);
  const [showUpdateEmailDialog, setShowUpdateEmailDialog] = useState(false);
  const [showAdminResetDialog, setShowAdminResetDialog] = useState(false);


  // Change password fields
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');

  // Set PIN fields
  const [pinPassword, setPinPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [setPinError, setSetPinError] = useState('');

  // Update email fields
  const [emailPassword, setEmailPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [updateEmailError, setUpdateEmailError] = useState('');

  // Password reset fields
  const [resetToken, setResetToken] = useState('');
  const [resetUserId, setResetUserId] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');

  // Admin: reset another user's password/PIN fields
  const [adminTargetUserId, setAdminTargetUserId] = useState('');
  const [adminNewPassword, setAdminNewPassword] = useState('');
  const [adminConfirmPassword, setAdminConfirmPassword] = useState('');
  const [adminNewQuickPin, setAdminNewQuickPin] = useState('');
  const [adminResetError, setAdminResetError] = useState('');
  const [adminResetSuccess, setAdminResetSuccess] = useState('');
  const [adminResetLoading, setAdminResetLoading] = useState(false);

  const ADMIN_USER_ID = 'iammrfrank';


  // Check for existing auth on mount
  useEffect(() => {
    const token = apiClient.getToken();
    const savedUserId = apiClient.getUserId();

    if (token && savedUserId) {
      // We have a token, validate it by fetching user progress from the server
      setUserId(savedUserId);
      setIsAuthenticated(true);
      
      // Fetch the user's actual progress from the server to restore correct level
      const fetchUserProgress = async () => {
        try {
          const result = await apiClient.request('/user-progress');
          if (result.response.ok && result.data) {
            setIsLoading(false);
            if (onUserChange) onUserChange(savedUserId);
            onUserLoaded({
              normalLevel: result.data.normalLevel || 1,
              hardLevel: result.data.hardLevel || 1,
              userId: savedUserId,
              isDarkMode: result.data.isDarkMode
            });
          } else {
            // If the request fails (e.g., expired token), fall back to defaults
            setIsLoading(false);
            onUserLoaded({
              normalLevel: 1,
              hardLevel: 1,
              userId: savedUserId
            });
          }
        } catch (err) {
          console.error('Failed to fetch user progress on refresh:', err);
          setIsLoading(false);
          // Fall back to defaults - WebSocket will handle game resume
          onUserLoaded({
            normalLevel: 1,
            hardLevel: 1,
            userId: savedUserId
          });
        }
      };
      
      fetchUserProgress();
    } else {
      setIsLoading(false);
    }

    // Set up auth error handler
    apiClient.setAuthErrorHandler(() => {
      setIsAuthenticated(false);
      setUserId('');
      setShowAuthModal(true);
      setError('Your session has expired. Please log in again.');
      if (onUserChange) onUserChange(null);
    });

    // Check for password reset URL params
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const userIdParam = urlParams.get('userId');
    if (tokenParam && userIdParam) {
      setResetToken(tokenParam);
      setResetUserId(userIdParam);
      setShowResetForm(true);
      setShowAuthModal(true);
      setAuthTab('forgot');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFormFields = useCallback(() => {
    setPassword('');
    setConfirmPassword('');
    setEmail('');
    setPin('');
    setError('');
    setSuccessMessage('');
  }, []);

  const handleLogin = async () => {
    if (!inputId) {
      setError('Please enter your User ID');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await apiClient.login({ userId: inputId, password });

      if (result.ok) {
        setUserId(result.data.userId);
        setIsAuthenticated(true);
        setShowAuthModal(false);
        clearFormFields();
        setInputId('');

        if (onUserChange) onUserChange(result.data.userId);

        onUserLoaded({
          normalLevel: result.data.normalLevel || 1,
          hardLevel: result.data.hardLevel || 1,
          userId: result.data.userId,
          isDarkMode: result.data.isDarkMode
        });
      } else {
        setError(result.data.error || 'Invalid User ID or password');
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
    }

    setIsLoading(false);
  };

  const handlePinLogin = async () => {
    if (!inputId) {
      setError('Please enter your User ID');
      return;
    }
    if (!pin) {
      setError('Please enter your PIN');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await apiClient.loginWithPin({ userId: inputId, pin });

      if (result.ok) {
        setUserId(result.data.userId);
        setIsAuthenticated(true);
        setShowAuthModal(false);
        clearFormFields();
        setInputId('');

        if (onUserChange) onUserChange(result.data.userId);

        onUserLoaded({
          normalLevel: result.data.normalLevel || 1,
          hardLevel: result.data.hardLevel || 1,
          userId: result.data.userId,
          isDarkMode: result.data.isDarkMode
        });
      } else {
        if (result.data.error === 'No PIN set for this account') {
          setError('No PIN set for this account. Please log in with your password.');
          setUsePinLogin(false);
        } else {
          setError(result.data.error || 'Invalid User ID or PIN');
        }
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
    }

    setIsLoading(false);
  };

  const handleRegister = async () => {
    if (!inputId) {
      setError('Please enter a User ID');
      return;
    }
    if (inputId.length < 4 || inputId.length > 32) {
      setError('User ID must be between 4 and 32 characters');
      return;
    }
    if (!/^[a-zA-Z0-9]+$/.test(inputId)) {
      setError('User ID can only contain letters and numbers');
      return;
    }
    if (!password) {
      setError('Please enter a password');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password.length > 64) {
      setError('Password must be less than 64 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!email) {
      setError('Please enter an email address');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await apiClient.register({ userId: inputId, password, email });

      if (result.ok) {
        setUserId(result.data.userId);
        setIsAuthenticated(true);
        setShowAuthModal(false);
        clearFormFields();
        setInputId('');

        if (onUserChange) onUserChange(result.data.userId);

        onUserLoaded({
          normalLevel: result.data.normalLevel || 1,
          hardLevel: result.data.hardLevel || 1,
          userId: result.data.userId
        });
      } else {
        setError(result.data.error || 'Failed to create account');
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
    }

    setIsLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!inputId) {
      setError('Please enter your User ID');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await apiClient.requestPasswordReset({ userId: inputId });

      if (result.ok) {
        setSuccessMessage('If an account exists with that User ID, a password reset email has been sent.');
        setError('');
      } else {
        // Generic message to prevent user enumeration
        setSuccessMessage('If an account exists with that User ID, a password reset email has been sent.');
        setError('');
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
    }

    setIsLoading(false);
  };

  const handleResetPassword = async () => {
    if (!resetNewPassword) {
      setError('Please enter a new password');
      return;
    }
    if (resetNewPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await apiClient.resetPassword({
        token: resetToken,
        userId: resetUserId,
        newPassword: resetNewPassword
      });

      if (result.ok) {
        setSuccessMessage('Password reset successfully! You can now log in with your new password.');
        setShowResetForm(false);
        setResetToken('');
        setResetUserId('');
        setResetNewPassword('');
        setResetConfirmPassword('');
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        // Switch to login tab after delay
        setTimeout(() => {
          setAuthTab('login');
          setSuccessMessage('');
        }, 3000);
      } else {
        setError(result.data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
    }

    setIsLoading(false);
  };

  const handleChangePassword = async () => {
    if (!oldPassword) {
      setChangePasswordError('Please enter your current password');
      return;
    }
    if (!newPassword) {
      setChangePasswordError('Please enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      setChangePasswordError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setChangePasswordError('New passwords do not match');
      return;
    }

    try {
      const result = await apiClient.changePassword({
        oldPassword,
        newPassword
      });

      if (result.ok) {
        setShowChangePasswordDialog(false);
        setOldPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setChangePasswordError('');
        alert('Password changed successfully!');
      } else {
        setChangePasswordError(result.data.error || 'Failed to change password');
      }
    } catch (err) {
      setChangePasswordError('Unable to connect to server.');
    }
  };

  const handleSetPin = async () => {
    if (!pinPassword) {
      setSetPinError('Please enter your password to verify');
      return;
    }
    if (!newPin) {
      setSetPinError('Please enter a PIN');
      return;
    }
    if (newPin.length < 4 || newPin.length > 8) {
      setSetPinError('PIN must be 4-8 digits');
      return;
    }
    if (!/^\d+$/.test(newPin)) {
      setSetPinError('PIN must contain only numbers');
      return;
    }
    if (newPin !== confirmNewPin) {
      setSetPinError('PINs do not match');
      return;
    }

    try {
      const result = await apiClient.setQuickPin({
        password: pinPassword,
        pin: newPin
      });

      if (result.ok) {
        setShowSetPinDialog(false);
        setPinPassword('');
        setNewPin('');
        setConfirmNewPin('');
        setSetPinError('');
        alert('Quick PIN set successfully! You can now use it for faster mobile login.');
      } else {
        setSetPinError(result.data.error || 'Failed to set PIN');
      }
    } catch (err) {
      setSetPinError('Unable to connect to server.');
    }
  };

  const handleUpdateEmail = async () => {
    if (!emailPassword) {
      setUpdateEmailError('Please enter your password to verify');
      return;
    }
    if (!newEmail) {
      setUpdateEmailError('Please enter a new email address');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setUpdateEmailError('Please enter a valid email address');
      return;
    }

    try {
      const result = await apiClient.updateEmail({
        password: emailPassword,
        email: newEmail
      });

      if (result.ok) {
        setShowUpdateEmailDialog(false);
        setEmailPassword('');
        setNewEmail('');
        setUpdateEmailError('');
        alert('Email updated successfully!');
      } else {
        setUpdateEmailError(result.data.error || 'Failed to update email');
      }
    } catch (err) {
      setUpdateEmailError('Unable to connect to server.');
    }
  };

  const handleAdminResetUserPassword = async () => {
    setAdminResetError('');
    setAdminResetSuccess('');

    if (!adminTargetUserId) {
      setAdminResetError('Please enter the target User ID');
      return;
    }
    if (!adminNewPassword) {
      setAdminResetError('Please enter a new password');
      return;
    }
    if (adminNewPassword.length < 6) {
      setAdminResetError('New password must be at least 6 characters');
      return;
    }
    if (adminNewPassword !== adminConfirmPassword) {
      setAdminResetError('Passwords do not match');
      return;
    }
    if (adminNewQuickPin && (adminNewQuickPin.length < 4 || adminNewQuickPin.length > 8)) {
      setAdminResetError('Quick PIN must be 4-8 digits');
      return;
    }

    setAdminResetLoading(true);
    try {
      const result = await apiClient.adminResetUserPassword({
        targetUserId: adminTargetUserId,
        newPassword: adminNewPassword,
        newQuickPin: adminNewQuickPin || undefined
      });

      if (result.ok) {
        setAdminResetSuccess(result.data.message || `Password reset successfully for ${adminTargetUserId}`);
        setAdminTargetUserId('');
        setAdminNewPassword('');
        setAdminConfirmPassword('');
        setAdminNewQuickPin('');
      } else {
        setAdminResetError(result.data.error || 'Failed to reset password');
      }
    } catch (err) {
      setAdminResetError('Unable to connect to server.');
    }
    setAdminResetLoading(false);
  };

  const handleLogout = () => {

    apiClient.logout();
    setIsAuthenticated(false);
    setUserId('');
    setShowSettings(false);

    if (onUserChange) onUserChange(null);

    onUserLoaded({
      normalLevel: 1,
      hardLevel: 1,
      userId: null
    });

    window.location.reload();
  };

  const handleGuestPlay = () => {
    setShowAuthModal(false);
    clearFormFields();
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  // Loading state
  if (isLoading && !showAuthModal) {
    return <div className={`text-center p-4 ${theme.text}`}>Loading...</div>;
  }

  // Authenticated - show user bar with settings
  if (isAuthenticated && userId) {
    return (
      <div className={`flex items-center justify-between mb-4 ${theme.container} p-3 rounded-lg shadow-sm`}>
        <div className={`text-sm ${theme.text}`}>
          Player: <span className="font-semibold">{userId}</span>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`px-3 py-1 text-sm ${theme.button} rounded-md transition-colors flex items-center gap-1`}
          >
            Settings
            <span className="text-xs">{showSettings ? '▲' : '▼'}</span>
          </button>

          {/* Settings Dropdown */}
          {showSettings && (
            <>
              {/* Backdrop to close dropdown */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowSettings(false)}
              />
              <div className={`absolute right-0 top-full mt-1 ${theme.container} border ${theme.border} rounded-lg shadow-lg z-50 min-w-[200px]`}>
                <button
                  onClick={() => {
                    setShowSettings(false);
                    setShowChangePasswordDialog(true);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm ${theme.text} hover:bg-blue-100 dark:hover:bg-gray-600 rounded-t-lg transition-colors`}
                >
                  Change Password
                </button>
                <button
                  onClick={() => {
                    setShowSettings(false);
                    setShowSetPinDialog(true);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm ${theme.text} hover:bg-blue-100 dark:hover:bg-gray-600 transition-colors`}
                >
                  Set Quick PIN
                </button>
                <button
                  onClick={() => {
                    setShowSettings(false);
                    setShowUpdateEmailDialog(true);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm ${theme.text} hover:bg-blue-100 dark:hover:bg-gray-600 transition-colors`}
                >
                  Update Email
                </button>
                {userId === ADMIN_USER_ID && (
                  <>
                    <hr className={`${theme.border}`} />
                    <button
                      onClick={() => {
                        setShowSettings(false);
                        setShowAdminResetDialog(true);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm ${theme.text} hover:bg-blue-100 dark:hover:bg-gray-600 transition-colors`}
                    >
                      🛡️ Admin: Reset User Password
                    </button>
                  </>
                )}
                <hr className={`${theme.border}`} />
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-100 dark:hover:bg-red-900 rounded-b-lg transition-colors"
                >
                  Log Out
                </button>

              </div>
            </>
          )}
        </div>

        {/* Change Password Dialog */}
        {showChangePasswordDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowChangePasswordDialog(false)}>
            <div className={`${theme.container} p-6 rounded-lg shadow-xl w-96 max-w-full mx-4`} onClick={(e) => e.stopPropagation()}>
              <h3 className={`${theme.text} text-lg font-bold mb-4`}>Change Password</h3>
              <div className="space-y-3">
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Current password"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={64}
                />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password (min 6 characters)"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={64}
                />
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={64}
                  onKeyPress={(e) => handleKeyPress(e, handleChangePassword)}
                />
                {changePasswordError && (
                  <p className="text-red-500 text-sm">{changePasswordError}</p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => {
                      setShowChangePasswordDialog(false);
                      setOldPassword('');
                      setNewPassword('');
                      setConfirmNewPassword('');
                      setChangePasswordError('');
                    }}
                    className="px-4 py-2 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Set Quick PIN Dialog */}
        {showSetPinDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowSetPinDialog(false)}>
            <div className={`${theme.container} p-6 rounded-lg shadow-xl w-96 max-w-full mx-4`} onClick={(e) => e.stopPropagation()}>
              <h3 className={`${theme.text} text-lg font-bold mb-2`}>Set Quick PIN</h3>
              <p className={`text-sm ${theme.subtext} mb-4`}>
                Set a short numeric PIN for faster login on mobile devices.
              </p>
              <div className="space-y-3">
                <input
                  type="password"
                  value={pinPassword}
                  onChange={(e) => setPinPassword(e.target.value)}
                  placeholder="Enter your password to verify"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={64}
                />
                <input
                  type="tel"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="New PIN (4-8 digits)"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="tel"
                  value={confirmNewPin}
                  onChange={(e) => setConfirmNewPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Confirm PIN"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onKeyPress={(e) => handleKeyPress(e, handleSetPin)}
                />
                {setPinError && (
                  <p className="text-red-500 text-sm">{setPinError}</p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => {
                      setShowSetPinDialog(false);
                      setPinPassword('');
                      setNewPin('');
                      setConfirmNewPin('');
                      setSetPinError('');
                    }}
                    className="px-4 py-2 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSetPin}
                    className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Set PIN
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Update Email Dialog */}
        {showUpdateEmailDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowUpdateEmailDialog(false)}>
            <div className={`${theme.container} p-6 rounded-lg shadow-xl w-96 max-w-full mx-4`} onClick={(e) => e.stopPropagation()}>
              <h3 className={`${theme.text} text-lg font-bold mb-4`}>Update Email</h3>
              <div className="space-y-3">
                <input
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  placeholder="Enter your password to verify"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={64}
                />
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="New email address"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  onKeyPress={(e) => handleKeyPress(e, handleUpdateEmail)}
                />
                {updateEmailError && (
                  <p className="text-red-500 text-sm">{updateEmailError}</p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => {
                      setShowUpdateEmailDialog(false);
                      setEmailPassword('');
                      setNewEmail('');
                      setUpdateEmailError('');
                    }}
                    className="px-4 py-2 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateEmail}
                    className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Update Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin: Reset User Password Dialog */}
        {showAdminResetDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAdminResetDialog(false)}>
            <div className={`${theme.container} p-6 rounded-lg shadow-xl w-96 max-w-full mx-4`} onClick={(e) => e.stopPropagation()}>
              <h3 className={`${theme.text} text-lg font-bold mb-2`}>🛡️ Admin: Reset User Password</h3>
              <p className={`text-sm ${theme.subtext} mb-4`}>
                Reset another user's password (and optionally their quick PIN). Use with care.
              </p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={adminTargetUserId}
                  onChange={(e) => setAdminTargetUserId(e.target.value)}
                  placeholder="Target User ID"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={32}
                />
                <input
                  type="password"
                  value={adminNewPassword}
                  onChange={(e) => setAdminNewPassword(e.target.value)}
                  placeholder="New password (min 6 characters)"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={64}
                />
                <input
                  type="password"
                  value={adminConfirmPassword}
                  onChange={(e) => setAdminConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={64}
                />
                <input
                  type="tel"
                  value={adminNewQuickPin}
                  onChange={(e) => setAdminNewQuickPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Optional: set new quick PIN (4-8 digits)"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onKeyPress={(e) => handleKeyPress(e, handleAdminResetUserPassword)}
                />
                <p className={`text-xs ${theme.subtext}`}>
                  Leave PIN blank to clear the target user's existing quick PIN (they'll need to use the new password to log in).
                </p>
                {adminResetError && (
                  <p className="text-red-500 text-sm">{adminResetError}</p>
                )}
                {adminResetSuccess && (
                  <p className="text-green-500 text-sm">{adminResetSuccess}</p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => {
                      setShowAdminResetDialog(false);
                      setAdminTargetUserId('');
                      setAdminNewPassword('');
                      setAdminConfirmPassword('');
                      setAdminNewQuickPin('');
                      setAdminResetError('');
                      setAdminResetSuccess('');
                    }}
                    className="px-4 py-2 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleAdminResetUserPassword}
                    className="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                    disabled={adminResetLoading}
                  >
                    {adminResetLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Not authenticated - show login/guest button bar

  return (
    <div className={`mb-4 p-3 ${theme.container} rounded-lg shadow-sm`}>
      <div className="flex gap-2">
        <button
          onClick={() => {
            setShowAuthModal(true);
            setAuthTab('login');
            clearFormFields();
          }}
          className="px-4 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
        >
          Login / Create Account
        </button>
        <button
          onClick={handleGuestPlay}
          className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
        >
          Play as Guest
        </button>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm" onClick={() => setShowAuthModal(false)}>
          <div className={`${theme.container} p-6 rounded-xl shadow-2xl w-[420px] max-w-[95vw] mx-4 max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold ${theme.text}`}>🧪 Tubes</h2>
              <p className={`text-sm ${theme.subtext} mt-1`}>Tubes</p>
            </div>

            {/* Password Reset Form (from email link) */}
            {showResetForm ? (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold ${theme.text}`}>Reset Your Password</h3>
                <p className={`text-sm ${theme.subtext}`}>User ID: <strong>{resetUserId}</strong></p>
                <input
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  placeholder="New password (min 6 characters)"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={64}
                />
                <input
                  type="password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                  maxLength={64}
                  onKeyPress={(e) => handleKeyPress(e, handleResetPassword)}
                />
                {error && <p className="text-red-500 text-sm">{error}</p>}
                {successMessage && <p className="text-green-500 text-sm">{successMessage}</p>}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowResetForm(false);
                      setResetToken('');
                      setResetUserId('');
                      window.history.replaceState({}, document.title, window.location.pathname);
                    }}
                    className="flex-1 px-4 py-2 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    className="flex-1 px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Tab Switcher */}
                {authTab !== 'forgot' && (
                  <div className="flex mb-6 border-b border-gray-300 dark:border-gray-600">
                    <button
                      onClick={() => { setAuthTab('login'); clearFormFields(); }}
                      className={`flex-1 pb-3 text-sm font-medium transition-colors ${
                        authTab === 'login'
                          ? 'border-b-2 border-blue-500 text-blue-500'
                          : `${theme.subtext} hover:text-blue-400`
                      }`}
                    >
                      Login
                    </button>
                    <button
                      onClick={() => { setAuthTab('register'); clearFormFields(); }}
                      className={`flex-1 pb-3 text-sm font-medium transition-colors ${
                        authTab === 'register'
                          ? 'border-b-2 border-green-500 text-green-500'
                          : `${theme.subtext} hover:text-green-400`
                      }`}
                    >
                      Create Account
                    </button>
                  </div>
                )}

                {/* Login Tab */}
                {authTab === 'login' && (
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-1`}>User ID</label>
                      <input
                        type="text"
                        value={inputId}
                        onChange={(e) => setInputId(e.target.value)}
                        placeholder="Enter your User ID"
                        className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                        maxLength={32}
                        autoFocus
                      />
                    </div>

                    {usePinLogin ? (
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-1`}>PIN</label>
                        <input
                          type="tel"
                          value={pin}
                          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                          placeholder="Enter your PIN"
                          className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                          maxLength={8}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          onKeyPress={(e) => handleKeyPress(e, handlePinLogin)}
                        />
                      </div>
                    ) : (
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-1`}>Password</label>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                          maxLength={64}
                          onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                        />
                      </div>
                    )}

                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    {successMessage && <p className="text-green-500 text-sm">{successMessage}</p>}

                    <button
                      onClick={usePinLogin ? handlePinLogin : handleLogin}
                      className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Logging in...' : 'Login'}
                    </button>

                    <div className="flex items-center justify-between text-sm">
                      <button
                        onClick={() => setUsePinLogin(!usePinLogin)}
                        className="text-blue-500 hover:text-blue-600 transition-colors"
                      >
                        {usePinLogin ? '🔑 Use password instead' : '📱 Login with PIN'}
                      </button>
                      <button
                        onClick={() => {
                          setAuthTab('forgot');
                          setError('');
                          setSuccessMessage('');
                        }}
                        className="text-orange-500 hover:text-orange-600 transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>

                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className={`w-full border-t ${theme.border}`}></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className={`px-2 ${theme.container} ${theme.subtext}`}>or</span>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setShowAuthModal(false);
                        clearFormFields();
                      }}
                      className={`w-full px-4 py-2 ${theme.button} rounded-md transition-colors ${theme.text}`}
                    >
                      Play as Guest
                    </button>
                  </div>
                )}

                {/* Register Tab */}
                {authTab === 'register' && (
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-1`}>User ID</label>
                      <input
                        type="text"
                        value={inputId}
                        onChange={(e) => setInputId(e.target.value)}
                        placeholder="Choose a User ID (4-32 chars)"
                        className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                        maxLength={32}
                        autoFocus
                      />
                      <p className={`text-xs ${theme.subtext} mt-1`}>Letters and numbers only</p>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-1`}>Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Choose a password (min 6 characters)"
                        className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                        maxLength={64}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-1`}>Confirm Password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter your password"
                        className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                        maxLength={64}
                      />
                      {password && confirmPassword && password !== confirmPassword && (
                        <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                      )}
                      {password && confirmPassword && password === confirmPassword && (
                        <p className="text-green-500 text-xs mt-1">✓ Passwords match</p>
                      )}
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-1`}>Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="For password recovery"
                        className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                        onKeyPress={(e) => handleKeyPress(e, handleRegister)}
                      />
                      <p className={`text-xs ${theme.subtext} mt-1`}>Used only for password recovery</p>
                    </div>

                    {error && <p className="text-red-500 text-sm">{error}</p>}

                    <button
                      onClick={handleRegister}
                      className="w-full px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Creating Account...' : 'Create Account'}
                    </button>

                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className={`w-full border-t ${theme.border}`}></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className={`px-2 ${theme.container} ${theme.subtext}`}>or</span>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setShowAuthModal(false);
                        clearFormFields();
                      }}
                      className={`w-full px-4 py-2 ${theme.button} rounded-md transition-colors ${theme.text}`}
                    >
                      Play as Guest
                    </button>
                  </div>
                )}

                {/* Forgot Password Tab */}
                {authTab === 'forgot' && (
                  <div className="space-y-4">
                    <h3 className={`text-lg font-semibold ${theme.text}`}>Forgot Password</h3>
                    <p className={`text-sm ${theme.subtext}`}>
                      Enter your User ID and we'll send a password reset link to your registered email.
                    </p>
                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-1`}>User ID</label>
                      <input
                        type="text"
                        value={inputId}
                        onChange={(e) => setInputId(e.target.value)}
                        placeholder="Enter your User ID"
                        className={`w-full px-3 py-2 border rounded-md ${theme.input} ${theme.text}`}
                        maxLength={32}
                        autoFocus
                        onKeyPress={(e) => handleKeyPress(e, handleForgotPassword)}
                      />
                    </div>

                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    {successMessage && <p className="text-green-500 text-sm">{successMessage}</p>}

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setAuthTab('login');
                          setError('');
                          setSuccessMessage('');
                        }}
                        className="flex-1 px-4 py-2 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600"
                      >
                        Back to Login
                      </button>
                      <button
                        onClick={handleForgotPassword}
                        className="flex-1 px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Sending...' : 'Send Reset Email'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Close button */}
            <button
              onClick={() => {
                setShowAuthModal(false);
                clearFormFields();
              }}
              className={`absolute top-3 right-3 ${theme.subtext} hover:${theme.text} text-xl transition-colors`}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthModal;
